import { z } from "zod";
import { CustomFormFieldValidation } from "@shared/types";

interface CustomValidations {
  cnpj: CustomFormFieldValidation;
  volumetryEmailDay: string;
  invalidEmail: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function emailListSchema(message: string) {
  return z
    .string()
    .max(1000)
    .optional()
    .nullable()
    .refine((value) => {
      if (!value) return true;
      return value
        .split(/[;,]/)
        .map((item) => item.trim())
        .filter(Boolean)
        .every((email) => EMAIL_PATTERN.test(email));
    }, message);
}

export const agreementSchema = ({ cnpj, volumetryEmailDay, invalidEmail }: CustomValidations) => {
  return z.object({
    name: z.string().min(1),
    supervisor: z.any().optional().nullable(),
    plan: z.any().optional(),
    levels: z.array(z.any()).min(1),
    value: z.string().optional().nullable(),
    start_date: z.any().optional().nullable(),
    received_books_start_date: z.any().optional().nullable(),
    email: z.string().optional().nullable(),
    cnpj: z.string().optional().nullable().refine((value) => !value || cnpj.validate(value), cnpj.errorMessage),
    site: z.string().optional().nullable(),
    url_app_ios: z.string().optional().nullable(),
    slug_app: z.string().optional().nullable(),
    slug_app_android: z.string().optional().nullable(),
    slug_app_ios: z.string().optional().nullable(),
    active: z.boolean().optional().nullable(),
    user_limit: z.any().optional().nullable(),
    promocode_limit: z.string().optional().nullable(),
    promocode_module: z.boolean().optional().nullable(),
    cpf_module: z.boolean().optional().nullable(),
    external_code_subscription_module: z.boolean().optional().nullable(),
    physical_card_module: z.boolean().optional().nullable(),
    show_best_sellers: z.boolean().optional().nullable(),
    local_module: z.boolean().optional().nullable(),
    ecommerce_module: z.boolean().optional().nullable(),
    bookstore_module: z.boolean().optional().nullable(),
    enable_sending_book_email: z.boolean().optional().nullable(),
    book_sectors: z.array(z.number()).optional().nullable(),
    ex_client_module: z.boolean().optional().nullable(),
    referral_module: z.boolean().optional().nullable(),
    econometer_module: z.boolean().optional().nullable(),
    customer_central_module: z.boolean().optional().nullable(),
    be_partner_module: z.boolean().optional().nullable(),
    plan_sale_module: z.boolean().optional().nullable(),
    digital_magazines_module: z.boolean().optional().nullable(),
    push_notifications_module: z.boolean().optional().nullable(),
    has_integrated_purchase_on_mobile_store: z.boolean().optional().nullable(),
    allow_default_plan_on_integration: z.boolean().optional().nullable(),
    same_subscription_for_supervised_agreements: z.boolean().optional().nullable(),
    ura_module_cellphone: z.string().optional().nullable(),
    bookstore_types: z.array(z.string()).optional().nullable(),
    hide_companies: z.array(z.string()).optional().nullable(),
    hide_categories: z.array(z.string()).optional().nullable(),
    logo: z.any().optional().nullable(),
    menu_color: z.string().optional().nullable(),
    menu_font_color: z.string().optional().nullable(),
    primary_color: z.string().optional().nullable(),
    primary_font_color: z.string().optional().nullable(),
    secondary_color: z.string().optional().nullable(),
    secondary_font_color: z.string().optional().nullable(),
    in_app_landing: z.boolean().optional().nullable(),
    referral_offer: z.string().optional().nullable(),
    referral_value: z.string().optional().nullable(),
    referral_image: z.any().optional().nullable(),
    legal_text: z.string().optional().nullable(),
    privacy_policy: z.string().optional().nullable(),
    integration_id: z.string().optional().nullable(),
    integration_host: z.string().url().optional().nullable(),
    integration_client_id: z.string().optional().nullable(),
    integration_token: z.string().optional().nullable(),
    integration_token2: z.string().optional().nullable(),
    integration_name: z.string().optional().nullable(),
    integration_password: z.string().optional().nullable(),
    integrated_login: z.string().optional().nullable(),
    idp: z.string().optional().nullable(),
    is_default_agreement: z.boolean().optional().nullable(),
    agreement_for_login_use: z.number().optional().nullable(),
    integration_special_case: z.boolean().optional().nullable(),
    can_use_dataless_integration: z.boolean().optional().nullable(),
    third_party_login: z.boolean().optional().nullable(),
    chat_widget_script: z.string().optional().nullable(),
    product_module: z.boolean().optional().nullable(),
    razao_social: z.string().max(255).optional().nullable(),
    nome_fantasia: z.string().max(255).optional().nullable(),
    financial_email: emailListSchema(invalidEmail),
    provider_owner_email: emailListSchema(invalidEmail),
    financial_dashboard: z.boolean().optional().nullable(),
    volumetry_email_automatic: z.boolean().optional().nullable(),
    volumetry_email_day: z.union([z.number(), z.string(), z.null()]).optional(),
  }).superRefine((values, ctx) => {
    if (!values.volumetry_email_automatic) return;
    const day = Number(values.volumetry_email_day);
    if (!Number.isInteger(day) || day < 1 || day > 31) {
      ctx.addIssue({
        code: "custom",
        path: ["volumetry_email_day"],
        message: volumetryEmailDay,
      });
    }
  });
};

export type AgreementFormValues = z.infer<ReturnType<typeof agreementSchema>>;
